// src/providers.tsx
// Client-side provider tree — QueryClientProvider for TanStack Query plus a
// global Toaster mount. The QueryClient's MutationCache has a default onError
// handler so every mutation that lacks its own onError still surfaces failures
// to the user via toast.

import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Toaster, toast } from 'react-hot-toast'
import { isAppError } from '@/lib/errors'
import { ApiClientError } from '@/src/lib/api-client'

/**
 * Normalize any thrown value into a user-facing message. Handles AppError,
 * ApiClientError (bridge error envelope), the bridge's `{ error: { message } }`
 * shape, and plain Error. Falls back to "Something went wrong" for unknowns.
 */
export function getErrorMessage(error: unknown): string {
  if (isAppError(error)) return error.message
  if (error instanceof ApiClientError) return error.message
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    // Bridge error envelope: { error: { code, message } }
    const maybe = error as { error?: { message?: unknown }; message?: unknown }
    if (typeof maybe.error?.message === 'string') return maybe.error.message
    if (typeof maybe.message === 'string') return maybe.message
  }
  return 'Something went wrong'
}

function makeQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // If the mutation defined its own onError, it has already handled
        // (or chosen to ignore) the error — don't double-toast.
        if (mutation.options.onError) return
        toast.error(getErrorMessage(error))
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  })
}

interface ProvidersProps {
  children: ReactNode
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(makeQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <Toaster
        position="top-right"
        gutter={8}
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--color-surface-raised)',
            color: 'var(--color-foreground)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            padding: '0.625rem 0.875rem',
            boxShadow:
              '0 10px 15px -3px rgb(0 0 0 / 0.4), 0 4px 6px -4px rgb(0 0 0 / 0.4)',
          },
          success: {
            iconTheme: {
              primary: 'var(--color-success)',
              secondary: 'var(--color-surface-raised)',
            },
            ariaProps: {
              role: 'status',
              'aria-live': 'polite',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: 'var(--color-destructive)',
              secondary: 'var(--color-foreground-on-destructive)',
            },
            style: {
              background: 'var(--color-destructive-subtle)',
              color: 'var(--color-foreground)',
              border: '1px solid var(--color-destructive)',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              padding: '0.625rem 0.875rem',
            },
            ariaProps: {
              role: 'alert',
              'aria-live': 'assertive',
            },
          },
        }}
      />
    </QueryClientProvider>
  )
}
